/** @format */

import {useEffect} from 'react';

import {Box} from '@mui/material';

import {CheckCircleOutline} from '@mui/icons-material';

import {Controller, SubmitHandler, useForm} from 'react-hook-form';

import {yupResolver} from '@hookform/resolvers/yup';

import {Trans, useTranslation} from 'react-i18next';

import {TextFieldWrap} from 'components/InputsNew/TextFieldWrap';

import {useApolloError} from 'helpers/hooks/useApolloError';

import {InputRow, InputsList, LabelWrap, Root, SubmitButton} from './styles';
import {useRequestAgencyTariffsMutation} from './graphql/__generated__';
import {validationSchema} from './validationSchema';
import {FormState} from './types';
import {
  DEFAULT_VALUES,
  ID_FORM_ROOT,
  ID_INPUT_COMMENT,
  ID_INPUT_INN,
  ID_INPUT_LINK,
  ID_INPUT_NAME,
  ID_INPUT_PHONE,
  ID_SUBMIT_BUTTON,
  MAX_COMMENT,
  MAX_INN,
  MAX_LINK,
  MAX_NAME,
  MAX_PHONE,
} from './constants';
import {FieldsLeft} from './FieldsLeft';

interface Props {
  onSuccess: () => void;
}

const AgencyForm = ({onSuccess}: Props) => {
  const {t} = useTranslation();
  const {onError, isError, renderAlert} = useApolloError();

  const {
    control,
    handleSubmit,
    formState: {errors, isValid},
    trigger,
    setFocus,
  } = useForm({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const [request, {loading}] = useRequestAgencyTariffsMutation();

  useEffect(() => {
    // Ошибки полей должны сразу показываться при загрузке компонента
    trigger(['inn', 'phone', 'link', 'name']);
  }, [trigger]);

  const sendRequest = async (state: FormState) => {
    try {
      const response = await request({
        variables: {
          name: state.name,
          phone: state.phone,
          site: state.link,
          inn: state.inn,
          comment: state.comment,
        },
      });
      if (response.data?.requestAgencyTariffs) {
        onSuccess();
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.log(e);
      onError();
    }
  };

  const onSubmit: SubmitHandler<FormState> = state => {
    return sendRequest(state);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} data-testid={ID_FORM_ROOT}>
      <Root>
        <InputsList>
          <InputRow>
            <LabelWrap htmlFor='name'>
              <Trans>Имя</Trans>
            </LabelWrap>
            <Controller
              name='name'
              control={control}
              render={({field}) => (
                <TextFieldWrap
                  {...field}
                  id='name'
                  placeholder={t('Как к вам обращаться?')}
                  maxLength={MAX_NAME}
                  slotProps={{
                    htmlInput: {
                      ['data-testid']: ID_INPUT_NAME,
                    },
                  }}
                />
              )}
            />
          </InputRow>

          <InputRow>
            <LabelWrap htmlFor='phone' maxWidth='100px'>
              <Trans>Телефон для связи</Trans>
            </LabelWrap>
            <Controller
              name='phone'
              control={control}
              render={({field}) => (
                <TextFieldWrap
                  {...field}
                  id='phone'
                  placeholder='+7'
                  type='tel'
                  maxLength={MAX_PHONE}
                  slotProps={{
                    htmlInput: {
                      ['data-testid']: ID_INPUT_PHONE,
                    },
                  }}
                />
              )}
            />
          </InputRow>

          <InputRow>
            <LabelWrap htmlFor='link'>
              <Trans>Сайт компании</Trans>
            </LabelWrap>
            <Controller
              name='link'
              control={control}
              render={({field}) => (
                <TextFieldWrap
                  {...field}
                  id='link'
                  placeholder={t('Добавьте ссылку')}
                  maxLength={MAX_LINK}
                  slotProps={{
                    htmlInput: {
                      ['data-testid']: ID_INPUT_LINK,
                    },
                  }}
                />
              )}
            />
          </InputRow>

          <InputRow>
            <LabelWrap htmlFor='inn'>
              <Trans>ИНН</Trans>
            </LabelWrap>
            <Controller
              name='inn'
              control={control}
              render={({field}) => (
                <TextFieldWrap
                  {...field}
                  id='inn'
                  placeholder={t('Нам важно понимать, что вы — рекламное агентство')}
                  isNumber
                  maxLength={MAX_INN}
                  slotProps={{
                    htmlInput: {
                      ['data-testid']: ID_INPUT_INN,
                    },
                  }}
                />
              )}
            />
          </InputRow>

          <InputRow>
            <LabelWrap htmlFor='comment' isFlexStart>
              <Trans>Любые комментарии</Trans>
              <br />
              <Box component='span'>
                <Trans>Не обязательно</Trans>
              </Box>
            </LabelWrap>
            <Controller
              name='comment'
              control={control}
              render={({field}) => (
                <TextFieldWrap
                  {...field}
                  id='comment'
                  placeholder={t(
                    'Несколько слов о сотрудничестве. Приблизительный бюджет, пожелания, что угодно',
                  )}
                  multiline
                  maxRows={5}
                  maxLength={MAX_COMMENT}
                  slotProps={{
                    htmlInput: {
                      ['data-testid']: ID_INPUT_COMMENT,
                    },
                  }}
                />
              )}
            />
          </InputRow>
        </InputsList>

        <FieldsLeft errors={errors} onClickError={setFocus} />

        {renderAlert}

        <SubmitButton
          type='submit'
          disabled={loading || !isValid || isError}
          data-testid={ID_SUBMIT_BUTTON}
        >
          {isValid && <CheckCircleOutline />}
          <Trans>Оставить заявку</Trans>
        </SubmitButton>
      </Root>
    </form>
  );
};

export {AgencyForm};
